import type { UserOnboarding } from "@brookbush/types";

import { ceuSelectedIds } from "./fields/preferencesValidation";
import type { WizardFormValues } from "./onboardingWizardForm";
import type { LearningPathChoice } from "~/hooks/useOnboarding";

/**
 * Assembles the onboarding payload from the wizard's form values and the chosen
 * learning path. Only the active path's data is included (certification id or
 * CEU goals), and `isOnboardingCompleted` is always set so the dashboard renders
 * the plan after this is applied. Conforms to `userOnboardingSchema`.
 *
 * @param {WizardFormValues} values - Current react-hook-form values
 * @param {LearningPathChoice | null} learningPath - Selected learning path
 * @returns {UserOnboarding} Payload for ProfileService.updateOnboarding
 */
export const buildOnboardingPayload = (
	values: WizardFormValues,
	learningPath: LearningPathChoice | null,
): UserOnboarding => {
	const payload: UserOnboarding = {
		primaryProfessionId: values.primaryProfessionId || null,
		secondaryProfessionId: values.secondaryProfessionId || null,
		cataMemberNumber: values.cataMemberNumber || null,
		isOnboardingCompleted: true,
	};

	if (learningPath === "certification") {
		payload.selectedCertificationId = values.selectedCertificationId || null;
	} else if (learningPath === "ceu") {
		payload.ceuGoals = ceuSelectedIds({ ceuGoals: values.ceuGoals }).map(
			(accreditationId) => {
				const { renewalDate } = values.ceuGoals[accreditationId] ?? {};
				return {
					accreditationId,
					renewalDate: renewalDate ? new Date(renewalDate).toISOString() : null,
				};
			},
		);
	}

	if (values.phoneNumber) {
		payload.phoneNumber = values.phoneNumber;
	}

	return payload;
};
